import React from 'react';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import { Add } from '@mui/icons-material';
import type { DraftSection } from './useFormDraft';

interface Props {
  sections: DraftSection[];
  selected: string | null;
  onSelect: (id: string) => void;
  onAdd: () => void;
}

/** The form's sections, in order: pick one to edit it. */
const SectionRail: React.FC<Props> = ({ sections, selected, onSelect, onAdd }) => (
  <Box sx={(t) => ({
    width: { md: 260 }, flexShrink: 0, overflow: 'auto', py: 2, px: 1.5,
    borderRight: { md: `1px solid ${t.palette.divider}` },
    borderBottom: { xs: `1px solid ${t.palette.divider}`, md: 'none' },
    bgcolor: alpha(t.palette.text.primary, 0.015),
  })}>
    <Typography variant="overline" color="text.secondary" sx={{ px: 1, letterSpacing: 0.8 }}>Sections</Typography>
    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
      {sections.map((s, i) => {
        const on = s.uid === selected;
        const shown = s.fields.filter(f => f.enabled !== false).length;
        return (
          <Box
            key={s.uid}
            component="button"
            type="button"
            onClick={() => onSelect(s.uid)}
            sx={(t) => ({
              all: 'unset', boxSizing: 'border-box', cursor: 'pointer', width: '100%',
              display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1, borderRadius: 2,
              bgcolor: on ? 'background.paper' : 'transparent',
              boxShadow: on ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.5)}` : 'none',
              '&:hover': { bgcolor: on ? 'background.paper' : alpha(t.palette.text.primary, 0.04) },
              '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${t.palette.primary.main}` },
            })}
          >
            <Typography variant="caption" color={on ? 'primary.main' : 'text.disabled'} sx={{ width: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {i + 1}
            </Typography>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={on ? 600 : 500} noWrap color={s.title.trim() ? 'text.primary' : 'text.disabled'}>
                {s.title.trim() || 'Untitled section'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {shown} question{shown === 1 ? '' : 's'}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Stack>
    <Button size="small" startIcon={<Add />} onClick={onAdd} sx={{ mt: 1, ml: 0.5, textTransform: 'none', fontWeight: 600 }}>
      Add section
    </Button>
  </Box>
);

export default React.memo(SectionRail);
